// Página PÚBLICA de la carta por QR de mesa (/carta/:token — fuera del login,
// patrón /verificar-contrato). El cliente ve el menú, arma su pedido y este
// entra como líneas PENDIENTES (origen 'qr') a la cuenta ABIERTA de la mesa;
// el mesero las confirma y comanda. Mobile-first.
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Minus, Plus, Send, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';

interface CartaProducto { id: string; nombre: string; precio: number; iva_tarifa: number; mods: boolean; imagen?: string | null }
interface CartaData {
  valido: boolean;
  mesa_numero?: number;
  empresa?: string;
  qr_autoservicio?: boolean;
  carta?: { categoria: string; productos: CartaProducto[] }[];
  cuenta?: {
    abierta: boolean; codigo: string; total: number;
    lineas: { descripcion: string; cantidad: number; estado: string; origen: string; subtotal: number }[];
  } | null;
}
interface ItemCarrito { producto_id: string; nombre: string; precio: number; cantidad: number; notas: string }

const usd = (v: number | string) => `$${Number(v).toFixed(2)}`;

export function CartaQr() {
  const { token } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const [carrito, setCarrito] = useState<ItemCarrito[]>([]);
  const [nota, setNota] = useState('');
  const [cat, setCat] = useState('');
  const [comensales, setComensales] = useState('2');

  const { data, isFetching } = useQuery<CartaData>({
    queryKey: ['qr-carta', token],
    queryFn: async () => (await apiClient.post('/rpc/rest_qr_carta', { _token: token })).data,
    enabled: !!token,
    staleTime: 0, refetchInterval: 20000,
  });

  const categorias = useMemo(() => data?.carta ?? [], [data]);
  const catActiva = categorias.find((c) => c.categoria === cat) ?? categorias[0];
  const total = carrito.reduce((a, i) => a + i.precio * i.cantidad, 0);

  const agregar = (p: CartaProducto) =>
    setCarrito((xs) => {
      const ex = xs.find((x) => x.producto_id === p.id);
      return ex ? xs.map((x) => x === ex ? { ...x, cantidad: x.cantidad + 1 } : x)
        : [...xs, { producto_id: p.id, nombre: p.nombre, precio: Number(p.precio), cantidad: 1, notas: '' }];
    });
  const restar = (i: ItemCarrito) =>
    setCarrito((xs) => i.cantidad <= 1 ? xs.filter((x) => x !== i)
      : xs.map((x) => x === i ? { ...x, cantidad: x.cantidad - 1 } : x));

  const pedir = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/rest_qr_pedir', {
      _token: token,
      _items: carrito.map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad, notas: i.notas || null })),
      _nota: nota || null,
    })).data,
    onSuccess: (r: { agregadas: number; mensaje: string }) => {
      setCarrito([]); setNota('');
      void qc.invalidateQueries({ queryKey: ['qr-carta'] });
      toast.success(r.mensaje ?? `Pedido enviado (${r.agregadas})`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Llamar al mesero / pedir la cuenta (734): aviso que vibra en el teléfono
  // del mesero (Mesero móvil). Anónimo por el token de la mesa, con dedupe.
  const llamar = useMutation({
    mutationFn: async (motivo: 'mesero' | 'cuenta') =>
      (await apiClient.post('/rpc/rest_qr_llamar', { _token: token, _motivo: motivo })).data,
    onSuccess: (_r, motivo) => toast.success(motivo === 'cuenta'
      ? 'Cuenta solicitada — el mesero viene en camino' : 'Mesero llamado — viene en camino'),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abrirMesa = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/rest_qr_abrir_cuenta', {
      _token: token, _comensales: Number(comensales) || 1,
    })).data,
    onSuccess: (r: { mensaje: string }) => {
      void qc.invalidateQueries({ queryKey: ['qr-carta'] });
      toast.success(r.mensaje ?? 'Mesa abierta');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isFetching && !data) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }
  if (data && !data.valido) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <UtensilsCrossed className="w-12 h-12 mx-auto text-muted-foreground" />
          <p className="mt-3 text-muted-foreground">Este código QR no corresponde a ninguna mesa.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20 pb-40">
      <header className="sticky top-0 z-10 bg-card border-b px-4 py-3">
        <div className="font-semibold flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5" /> {data?.empresa}
        </div>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 text-sm text-muted-foreground truncate">Mesa {data?.mesa_numero} · pide desde tu teléfono</div>
          <button className="shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium disabled:opacity-50"
                  disabled={llamar.isPending} onClick={() => llamar.mutate('mesero')}>🔔 Llamar mesero</button>
          <button className="shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium disabled:opacity-50"
                  disabled={llamar.isPending} onClick={() => llamar.mutate('cuenta')}>🧾 La cuenta</button>
        </div>
      </header>

      {!data?.cuenta?.abierta && !data?.qr_autoservicio && (
        <div className="m-4 p-3 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-sm">
          La mesa aún no tiene una cuenta abierta: pide al mesero que te siente para poder ordenar.
        </div>
      )}

      {!data?.cuenta?.abierta && data?.qr_autoservicio && (
        <div className="m-4 p-3 rounded-md border bg-card space-y-2">
          <div className="text-sm font-medium">Bienvenido — abre tu mesa para empezar a ordenar</div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">¿Cuántos son?</label>
            <input className="w-16 border rounded px-2 py-1 text-sm text-center" type="number" min="1" max="50"
                   value={comensales} onChange={(e) => setComensales(e.target.value)} />
            <button className="ml-auto px-4 py-1.5 rounded-md bg-primary text-primary-foreground font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    disabled={abrirMesa.isPending} onClick={() => abrirMesa.mutate()}>
              {abrirMesa.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UtensilsCrossed className="w-4 h-4" />}
              Abrir mi mesa
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">El mesero confirmará tus platos antes de enviarlos a cocina.</p>
        </div>
      )}

      <nav className="px-4 pt-3 flex gap-1.5 overflow-x-auto">
        {categorias.map((c) => (
          <button key={c.categoria} onClick={() => setCat(c.categoria)}
            className={`px-3 py-1 rounded-full border text-sm whitespace-nowrap ${catActiva?.categoria === c.categoria
              ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>
            {c.categoria}
          </button>
        ))}
      </nav>

      <main className="p-4 space-y-2">
        {(catActiva?.productos ?? []).map((p) => {
          const enCarrito = carrito.find((x) => x.producto_id === p.id);
          return (
            <div key={p.id} className="bg-card border rounded-lg px-3 py-2 flex items-center gap-3">
              {p.imagen && (
                <img src={p.imagen} alt={p.nombre} className="w-14 h-14 rounded-md object-cover flex-none" />
              )}
              <div className="flex-1">
                <div className="font-medium">{p.nombre}</div>
                <div className="text-sm text-muted-foreground">{usd(p.precio)}{p.mods ? ' · personalizable con el mesero' : ''}</div>
              </div>
              {enCarrito ? (
                <div className="flex items-center gap-2">
                  <button className="w-8 h-8 rounded-full border flex items-center justify-center" onClick={() => restar(enCarrito)}>
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-semibold w-5 text-center">{enCarrito.cantidad}</span>
                  <button className="w-8 h-8 rounded-full border flex items-center justify-center bg-primary text-primary-foreground"
                          onClick={() => agregar(p)}>
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button className="w-8 h-8 rounded-full border flex items-center justify-center bg-primary text-primary-foreground"
                        onClick={() => agregar(p)} disabled={!data?.cuenta?.abierta}>
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </main>

      {(data?.cuenta?.lineas ?? []).length > 0 && (
        <section className="px-4 pb-4">
          <h2 className="font-semibold text-sm mb-1">Mi cuenta ({data?.cuenta?.codigo})</h2>
          <div className="bg-card border rounded-lg divide-y text-sm">
            {(data?.cuenta?.lineas ?? []).map((l, i) => (
              <div key={i} className="px-3 py-1.5 flex justify-between gap-2">
                <span>{Number(l.cantidad)}× {l.descripcion}
                  {l.estado === 'pendiente' && <span className="text-xs text-amber-600"> · por confirmar</span>}
                </span>
                <span className="font-mono">{usd(l.subtotal)}</span>
              </div>
            ))}
            <div className="px-3 py-1.5 flex justify-between font-semibold">
              <span>Total</span><span className="font-mono">{usd(data?.cuenta?.total ?? 0)}</span>
            </div>
          </div>
        </section>
      )}

      {carrito.length > 0 && (
        <footer className="fixed bottom-0 inset-x-0 bg-card border-t p-3 space-y-2">
          <div className="text-sm space-y-1 max-h-28 overflow-auto">
            {carrito.map((i) => (
              <div key={i.producto_id} className="flex items-center justify-between gap-2">
                <span>{i.cantidad}× {i.nombre}</span>
                <input className="border rounded px-1.5 py-0.5 text-xs w-32" placeholder="nota (sin sal…)"
                       value={i.notas}
                       onChange={(e) => setCarrito((xs) => xs.map((x) => x === i ? { ...x, notas: e.target.value } : x))} />
                <span className="font-mono">{usd(i.precio * i.cantidad)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input className="flex-1 border rounded px-2 py-1.5 text-sm" placeholder="Nota general (opcional)"
                   value={nota} onChange={(e) => setNota(e.target.value)} />
            <button className="px-4 py-1.5 rounded-md bg-primary text-primary-foreground font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    disabled={pedir.isPending || !data?.cuenta?.abierta}
                    onClick={() => pedir.mutate()}>
              <Send className="w-4 h-4" /> Pedir {usd(total)}
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            El mesero confirmará tu pedido antes de enviarlo a cocina. Precios sin servicio/IVA finales.
          </p>
        </footer>
      )}
    </div>
  );
}
